import React, { useState } from "react";
import { Edit2, X, Send } from "lucide-react";
import supabase from "../supabaseClient";

const colorOptions = [
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#3b82f6", // blue
  "#8b5cf6", // purple
  "#ec4899", // pink
  "#6366f1", // indigo
];

const calendarUrl = (memberId) => {
  const base = (process.env.REACT_APP_SUPABASE_URL || "").replace(/^http:\/\//, "https://");
  return `${base}/functions/v1/family-ics?m=${memberId}&key=Neary4321`;
};

const normalizePhone = (raw) => {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
};

const MembersTab = ({ members, onReload, session }) => {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("#3b82f6");
  const [editBirthday, setEditBirthday] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [newBirthday, setNewBirthday] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [sendingLink, setSendingLink] = useState(null);
  const [sentLink, setSentLink] = useState(null);

  const handleEdit = (member) => {
    setEditingId(member.id);
    setEditName(member.name);
    setEditColor(member.color);
    setEditBirthday(member.birthday || "");
    setEditPhone(member.phone || "");
    setEditEmail(member.email || "");
  };

  const handleSaveEdit = async () => {
    try {
      const { error } = await supabase
        .from("family_members")
        .update({ name: editName, color: editColor, birthday: editBirthday || null, phone: normalizePhone(editPhone), email: editEmail || null })
        .eq("id", editingId);

      if (error) throw error;

      setEditingId(null);
      await onReload();
    } catch (error) {
      console.error("Error updating member:", error);
      alert("Error updating member: " + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this family member? This will also delete all their events."))
      return;

    try {
      const { error } = await supabase
        .from("family_members")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await onReload();
    } catch (error) {
      console.error("Error deleting member:", error);
      alert("Error deleting member: " + error.message);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      alert("Please enter a name");
      return;
    }

    try {
      const { error } = await supabase
        .from("family_members")
        .insert([{ name: newName, color: newColor, birthday: newBirthday || null, phone: normalizePhone(newPhone), email: newEmail || null }]);

      if (error) throw error;

      setAdding(false);
      setNewName("");
      setNewColor("#3b82f6");
      setNewBirthday("");
      setNewPhone("");
      await onReload();
    } catch (error) {
      console.error("Error adding member:", error);
      alert("Error adding member: " + error.message);
    }
  };

  const handleSendCalendarLink = async (member) => {
    if (!member.email) {
      alert("No email address saved for " + member.name);
      return;
    }
    setSendingLink(member.id);
    try {
      const url = calendarUrl(member.id);
      const webcalUrl = url.replace(/^https:\/\//, "webcal://");
      const html = `
        <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:520px;margin:0 auto;padding:24px 16px;">
          <h2 style="color:#7c3aed;margin:0 0 16px;">Neary Family Calendar</h2>
          <p style="font-size:15px;color:#374151;">Hi ${member.name}! Tap the button below to subscribe to your personal Neary Family Calendar on your iPhone.</p>
          <div style="margin:24px 0;">
            <a href="${webcalUrl}" style="display:inline-block;padding:12px 24px;background:#7c3aed;color:#fff;text-decoration:none;border-radius:8px;font-size:15px;font-weight:600;">Subscribe to Calendar</a>
          </div>
          <p style="font-size:13px;color:#6b7280;">Or go to <strong>Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</strong> and paste this link:</p>
          <p style="font-size:12px;color:#9ca3af;word-break:break-all;">${url}</p>
          <p style="margin-top:24px;font-size:12px;color:#9ca3af;">— Neary Family Calendar</p>
        </div>`;
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          contacts: [{ name: member.name, email: member.email }],
          subject: "Your Neary Family Calendar",
          html,
        },
      });
      if (error) throw error;
      setSentLink(member.id);
      setTimeout(() => setSentLink(null), 3000);
    } catch (err) {
      console.error("Error sending calendar link:", err);
      alert("Failed to send: " + err.message);
    } finally {
      setSendingLink(null);
    }
  };

  const handleLinkToAccount = async (memberId) => {
    if (!window.confirm("Link this family member to your account? This will show only their events in Personal view."))
      return;

    try {
      const { error } = await supabase
        .from("family_members")
        .update({ auth_user_id: session.user.id })
        .eq("id", memberId);

      if (error) throw error;

      await onReload();
      alert("Successfully linked to your account!");
    } catch (error) {
      console.error("Error linking member:", error);
      alert("Error linking member: " + error.message);
    }
  };

  const handleUnlinkFromAccount = async (memberId) => {
    if (!window.confirm("Unlink this family member from your account?"))
      return;

    try {
      const { error } = await supabase
        .from("family_members")
        .update({ auth_user_id: null })
        .eq("id", memberId);

      if (error) throw error;

      await onReload();
    } catch (error) {
      console.error("Error unlinking member:", error);
      alert("Error unlinking member: " + error.message);
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {session && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-xs sm:text-sm text-blue-800 mb-1">
            <strong>Logged in as:</strong> {session.user.email}
          </div>
          <div className="text-xs text-blue-600">
            Link a family member to your account to use Personal view
          </div>
        </div>
      )}

      {members.map((member) => {
        const isLinkedToCurrentUser = member.auth_user_id === session?.user?.id;

        return (
          <div key={member.id} className="p-3 sm:p-4 border border-gray-200 rounded-lg">
            {editingId === member.id ? (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: editColor }}
                  />
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-2 sm:py-3 border border-gray-300 rounded-lg text-base"
                  />
                </div>
                <div className="mb-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">Color:</div>
                  <div className="flex gap-2 flex-wrap">
                    {colorOptions.map((color) => (
                      <button key={color} type="button" onClick={() => setEditColor(color)}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full touch-manipulation ${editColor === color ? "ring-2 ring-offset-2 ring-purple-500" : ""}`}
                        style={{ backgroundColor: color }} />
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Birthday (optional)</label>
                  <input type="date" value={editBirthday} onChange={(e) => setEditBirthday(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="+12125551234"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (for calendar link)</label>
                  <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium touch-manipulation text-sm sm:text-base"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-4 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium touch-manipulation text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: member.color }}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm sm:text-base flex items-center gap-2">
                      {member.name}
                      {isLinkedToCurrentUser && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">You</span>
                      )}
                    </div>
                    {member.birthday && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        🎂 {new Date(member.birthday + "T00:00:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}
                      </div>
                    )}
                    {member.phone && (
                      <div className="text-xs text-gray-500 mt-0.5">{member.phone}</div>
                    )}
                  </div>
                  {member.email && (
                    <button
                      onClick={() => handleSendCalendarLink(member)}
                      disabled={sendingLink === member.id}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg touch-manipulation"
                      title="Email calendar subscription link"
                    >
                      {sentLink === member.id ? (
                        <span className="text-xs font-medium text-green-600">Sent!</span>
                      ) : (
                        <Send size={18} />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(member)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg touch-manipulation"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg touch-manipulation"
                  >
                    <X size={20} />
                  </button>
                </div>
                {isLinkedToCurrentUser ? (
                  <button
                    onClick={() => handleUnlinkFromAccount(member.id)}
                    className="w-full mt-2 px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 touch-manipulation"
                  >
                    Unlink from my account
                  </button>
                ) : !member.auth_user_id ? (
                  <button
                    onClick={() => handleLinkToAccount(member.id)}
                    className="w-full mt-2 px-3 py-2 text-xs sm:text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 touch-manipulation"
                  >
                    Link to my account
                  </button>
                ) : (
                  <div className="mt-2 text-xs text-gray-500 italic">
                    Linked to another account
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {adding ? (
        <div className="p-3 sm:p-4 border-2 border-purple-300 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0"
              style={{ backgroundColor: newColor }}
            />
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name"
              className="flex-1 px-3 py-2 sm:py-3 border border-gray-300 rounded-lg text-base"
            />
          </div>
          <div className="mb-3">
            <div className="text-sm font-medium text-gray-700 mb-2">Color:</div>
            <div className="flex gap-2 flex-wrap">
              {colorOptions.map((color) => (
                <button key={color} type="button" onClick={() => setNewColor(color)}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full touch-manipulation ${newColor === color ? "ring-2 ring-offset-2 ring-purple-500" : ""}`}
                  style={{ backgroundColor: color }} />
              ))}
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Birthday (optional)</label>
            <input type="date" value={newBirthday} onChange={(e) => setNewBirthday(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+12125551234"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (for calendar link)</label>
            <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              className="px-4 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium touch-manipulation text-sm sm:text-base"
            >
              Add
            </button>
            <button
              onClick={() => {
                setAdding(false);
                setNewName("");
                setNewColor("#3b82f6");
                setNewBirthday("");
                setNewPhone("");
                setNewEmail("");
              }}
              className="px-4 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium touch-manipulation text-sm sm:text-base"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="w-full py-3 sm:py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 font-medium touch-manipulation text-sm sm:text-base"
        >
          + Add Family Member
        </button>
      )}
    </div>
  );
};

export default MembersTab;
